import { useAutoAnimate } from '@formkit/auto-animate/react';
import { PlusIcon } from '@heroicons/react/20/solid';
import { useAppSelector } from 'app/hooks';
import {
  CeilingBelowRoofType,
  ExposureDirectionForCompassType,
  SizingItemsType,
  SkylightItemType
} from 'data/types';
import { nanoid } from 'nanoid';
import CompassPopoverWithHorizontal from '../compass-popover-with-horizontal';
import SkylightComponent from '../fenestration/skylight';

type CeilingFenestrationsProps = {
  ceiling: CeilingBelowRoofType;
  updateFn: (ceilingId: string, updates: Partial<CeilingBelowRoofType>) => void;
  updateDefaultSizingFn: (updates: Partial<SizingItemsType>) => void;
};

export default function CeilingFenestrations({
  ceiling,
  updateFn,
  updateDefaultSizingFn
}: CeilingFenestrationsProps) {
  const [animate] = useAutoAnimate({ duration: 100 });
  const { defaultSizingItems } = useAppSelector((state) => state.data);

  const addSkylight = (direction: ExposureDirectionForCompassType) => {
    const newSkylight: SkylightItemType = {
      ...defaultSizingItems.skylight,
      uniqueId: nanoid(5),
      valid: true,
      exposure: direction
    } as SkylightItemType;

    updateFn(ceiling.uniqueId, {
      skylights: [...(ceiling.skylights || []), newSkylight]
    });
  };

  const duplicateSkylight = (uniqueId: string) => {
    const skylight = (ceiling.skylights || []).find(
      (s) => s.uniqueId === uniqueId
    );

    if (skylight) {
      updateFn(ceiling.uniqueId, {
        skylights: [
          ...(ceiling.skylights || []),
          {
            ...skylight,
            uniqueId: nanoid(5)
          }
        ]
      });
    }
  };

  const deleteSkylight = (uniqueId: string) => {
    updateFn(ceiling.uniqueId, {
      skylights: (ceiling.skylights || []).filter(
        (skylight) => skylight.uniqueId !== uniqueId
      )
    });
  };

  return (
    <div className='flex flex-col gap-3 px-3 pb-3' ref={animate}>
      {(ceiling.skylights || []).map((skylight) => (
        <SkylightComponent
          key={skylight.uniqueId}
          skylight={skylight}
          updateFn={updateFn}
          duplicateFn={duplicateSkylight}
          deleteFn={deleteSkylight}
          updateDefaultSizingFn={updateDefaultSizingFn}
        />
      ))}
      <div className='flex items-center gap-3'>
        <CompassPopoverWithHorizontal
          label='Skylight'
          onClick={(direction) => addSkylight(direction)}
          icon={PlusIcon}
        />
      </div>
    </div>
  );
}
